// src/components/Control/ControlPanel/MapViewer.tsx
// Карта SLAM с робота (robot/<id>/map, публикует fleet_adapter на Pi), поза
// робота на ней (robot/<id>/pose) и текущий скан лидара (robot/<id>/scan).
//   map:  {"resolution", "width", "height", "origin": {x, y, yaw}, "png": base64}
//         PNG строка 0 — верх карты (+y), 1 пиксель = resolution м
//   pose: {"x", "y", "yaw"} в метрах/радианах в системе карты, yaw против часовой;
//         "v", "w" — скорость по одометрии (м/с, рад/с), "cmd": {"v", "w"} — пока
//         действует команда с сайта/голосом
//   scan: {"points": [[угол°, мм], ...]}, углы по часовой, 0 — вперёд робота
// По позам рисуется след робота, по скоростям — куда он едет в ближайшие
// PREDICT_S секунд (сплошная — по одометрии, пунктир — заданная командой).
// Предметы, которые робот заметил по дороге (robot/<id>/vision/objects, нода
// vision на Pi), — значками на карте; наведите мышь или нажмите, чтобы увидеть название.
// «Ехать по клику»: клик по карте шлёт цель {"x", "y"} в robot/<id>/goal, робот
// (mission_node, режим goto) строит путь по карте и едет; путь и цель приходят в
// robot/<id>/mission ("goal", "path", "result") и рисуются фиолетовым.
// Нажатие на робота — его карточка. Кнопка «Сохранить» шлёт {"cmd": "save", "name"}
// в robot/<id>/slam, результат приходит в robot/<id>/status → "slam".
// Карта занимает весь свой блок; нет карты (например, ESP32-робот без SLAM) —
// вместо неё вид лидара.
import React, { useEffect, useRef, useState } from 'react';
import { MapObject, MapObjectsMsg, objectIcon } from '../objectIcons';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import MapsPanel from './MapsPanel';
import LidarViewer from './LidarViewer';
import { useTelemetryState, useUiActions } from '../robot/RobotContext';
import { MODE_NAMES, STALE, fresh } from '../robot/telemetry';
import { useTick } from '../../../hooks/useTick';
import { Icon, usePopover } from '../Operator/ui';

interface MapViewerProps {
  zenohSession: zenoh.Session | null;
  robotId: string;
  compact?: boolean;          // превью: без панели, нажатие — onActivate
  onActivate?: () => void;
}

interface MapMsg {
  timestamp: number;
  resolution: number;
  width: number;
  height: number;
  origin: { x: number; y: number; yaw: number };
  png: string;
}

interface Pose {
  x: number;
  y: number;
  yaw: number;
  v?: number;
  w?: number;
  cmd?: { v: number; w: number } | null;
}

interface MissionStatus {
  mode: string;
  detail?: string;
  goal?: { x: number; y: number } | null;
  path?: [number, number][] | null;
  result?: string;
}

interface SlamStatus {
  ok: boolean | null;
  name?: string;
  path?: string;
  error?: string | null;
  saving?: boolean;
  loading?: boolean;
}

const POSE_STALE_MS = 3000;
// След: новая точка, когда робот сместился больше чем на TRAIL_STEP_M; не больше TRAIL_MAX точек
const TRAIL_STEP_M = 0.03;
const TRAIL_MAX = 5000;
// Горизонт прогноза движения, с
const PREDICT_S = 3;
// Радиус попадания по значку / роботу, CSS px (палец)
const HIT_PX = 18;

// Траектория по модели дифференциального привода при постоянных v, w
function predictPath(p: Pose, v: number, w: number): [number, number][] {
  const pts: [number, number][] = [[p.x, p.y]];
  let { x, y, yaw } = p;
  const dt = 0.1;
  for (let t = 0; t < PREDICT_S; t += dt) {
    x += v * Math.cos(yaw) * dt;
    y += v * Math.sin(yaw) * dt;
    yaw += w * dt;
    pts.push([x, y]);
  }
  return pts;
}

function readFlag(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}

function writeFlag(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* без запоминания */
  }
}

function payloadText(sample: zenoh.Sample): string {
  return sample.payload().toString();
}

// Карточка робота по нажатию на него на карте
const RobotCard: React.FC<{ x: number; y: number; pose: Pose | null; onClose: () => void }> = ({ x, y, pose, onClose }) => {
  useTick(1000);
  const { status, statusAt, mission, missionAt } = useTelemetryState(t => ({
    status: t.status, statusAt: t.statusAt, mission: t.mission, missionAt: t.missionAt,
  }));
  const { openSettings } = useUiActions();
  const st = fresh(statusAt, STALE.status) ? status : null;
  const m = fresh(missionAt, STALE.mission) ? mission : null;
  return (
    <div className="op-map-card" style={{ left: x, top: y }} role="dialog" aria-label="Карточка робота">
      <button type="button" className="op-icon-btn small" aria-label="Закрыть" onClick={onClose}><Icon name="close" size={14} /></button>
      <div className="op-pop-title">Робот</div>
      <p>{st ? (st.status === 'online' ? '● На связи' : '■ Офлайн') : '■ Нет статуса'}</p>
      <p className="op-muted">Батарея: {st?.battery != null ? `${st.battery}%${st.charging ? ' (заряжается)' : ''}` : '—'}</p>
      <p className="op-muted">Скорость: {st?.speed != null ? `${st.speed.toFixed(2)} м/с` : '—'}</p>
      <p className="op-muted">Режим: {m ? MODE_NAMES[m.mode] ?? m.mode : 'автономия не на связи'}</p>
      {pose && <p className="op-muted">Позиция: {pose.x.toFixed(2)}, {pose.y.toFixed(2)} м · {Math.round((pose.yaw * 180) / Math.PI)}°</p>}
      <button type="button" className="op-btn" onClick={() => openSettings('diagnostics')}>Диагностика</button>
    </div>
  );
};

const MapViewer: React.FC<MapViewerProps> = ({ zenohSession, robotId, compact, onActivate }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef({ w: 300, h: 300 });
  const mapRef = useRef<{ msg: MapMsg; img: HTMLImageElement } | null>(null);
  const poseRef = useRef<{ pose: Pose; at: number } | null>(null);
  const scanRef = useRef<[number, number][]>([]);
  const trailRef = useRef<[number, number][]>([]);
  const trailLenRef = useRef(0);
  const objectsRef = useRef<MapObject[]>([]);
  // Последнее преобразование мир -> холст (CSS px), для попадания по значкам и клика
  const viewRef = useRef<{
    toCanvas: (x: number, y: number) => [number, number];
    toWorld: (px: number, py: number) => [number, number];
  } | null>(null);
  const missionRef = useRef<MissionStatus | null>(null);
  const pendingGoalRef = useRef<{ x: number; y: number; at: number } | null>(null);
  const [clickGo, setClickGo] = useState(() => readFlag('map.clickGo', false));
  const [mission, setMission] = useState<MissionStatus | null>(null);
  const [showObjects, setShowObjects] = useState(() => readFlag('map.objects', true));
  const [objectCount, setObjectCount] = useState(0);
  const [hovered, setHovered] = useState<{ obj: MapObject; px: number; py: number; pinned?: boolean } | null>(null);
  const [robotCard, setRobotCard] = useState<{ px: number; py: number } | null>(null);
  const frameRef = useRef<number | null>(null);
  const [showTrail, setShowTrail] = useState(() => readFlag('map.trail', true));
  const [showVector, setShowVector] = useState(() => readFlag('map.vector', true));
  const optsRef = useRef({ showTrail, showVector, showObjects });
  optsRef.current = { showTrail, showVector, showObjects };
  const [trailInfo, setTrailInfo] = useState({ points: 0, meters: 0 });
  const [mapInfo, setMapInfo] = useState<string | null>(null);
  const [hasPose, setHasPose] = useState(false);
  const [slam, setSlam] = useState<SlamStatus | null>(null);
  const [mapsSettings, setMapsSettings] = useState<{ autosave_min: number; keep_auto: number } | null>(null);
  const [showMaps, setShowMaps] = useState(false);
  const [mapName, setMapName] = useState('');
  // Узкая карта (телефон, раскладка «Камера»): панель карты сворачивается в меню «Слои»
  const [narrow, setNarrow] = useState(false);
  const menuPop = usePopover();
  const savePop = usePopover();
  const infoPop = usePopover();
  const scanFresh = useTelemetryState(t => fresh(t.scanAt, STALE.scan));
  useTick(2000);

  const draw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const { w, h } = sizeRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#1e222c';
    ctx.fillRect(0, 0, w, h);
    const map = mapRef.current;
    if (!map) {
      viewRef.current = null;
      return;
    }
    const { msg, img } = map;

    // Вписываем карту в холст с сохранением пропорций
    const scale = Math.min(w / msg.width, h / msg.height);
    const dx = (w - msg.width * scale) / 2;
    const dy = (h - msg.height * scale) / 2;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, dx, dy, msg.width * scale, msg.height * scale);

    // мир (м) -> холст (px); origin.yaw у slam_toolbox всегда 0
    const toCanvas = (wx: number, wy: number): [number, number] => [
      dx + ((wx - msg.origin.x) / msg.resolution) * scale,
      dy + (msg.height - (wy - msg.origin.y) / msg.resolution) * scale,
    ];
    const toWorld = (px: number, py: number): [number, number] => [
      msg.origin.x + ((px - dx) / scale) * msg.resolution,
      msg.origin.y + (msg.height - (py - dy) / scale) * msg.resolution,
    ];
    viewRef.current = { toCanvas, toWorld };

    // Поездка к точке: путь и цель (пока робот не подтвердил — цель, куда кликнули)
    const m = missionRef.current;
    const goal = m?.mode === 'goto' && m.goal ? m.goal
      : pendingGoalRef.current && Date.now() - pendingGoalRef.current.at < 3000 ? pendingGoalRef.current : null;
    if (m?.mode === 'goto' && m.path && m.path.length > 1) {
      ctx.save();
      ctx.strokeStyle = '#b36bff';
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 6]);
      ctx.beginPath();
      m.path.forEach(([px, py], i) => {
        const [cx, cy] = toCanvas(px, py);
        if (i === 0) ctx.moveTo(cx, cy);
        else ctx.lineTo(cx, cy);
      });
      ctx.stroke();
      ctx.restore();
    }
    if (goal) {
      const [gx, gy] = toCanvas(goal.x, goal.y);
      ctx.save();
      ctx.strokeStyle = '#b36bff';
      ctx.fillStyle = 'rgba(179, 107, 255, 0.3)';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(gx, gy, 9, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(gx - 5, gy);
      ctx.lineTo(gx + 5, gy);
      ctx.moveTo(gx, gy - 5);
      ctx.lineTo(gx, gy + 5);
      ctx.stroke();
      ctx.restore();
    }

    // След робота
    const trail = trailRef.current;
    if (optsRef.current.showTrail && trail.length > 1) {
      ctx.save();
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      // Старая часть следа бледнее
      const n = trail.length;
      const parts = 4;
      for (let k = 0; k < parts; k++) {
        const a = Math.floor((k * (n - 1)) / parts);
        const b = Math.floor(((k + 1) * (n - 1)) / parts);
        if (b <= a) continue;
        ctx.strokeStyle = `rgba(79, 195, 247, ${0.3 + (0.6 * (k + 1)) / parts})`;
        ctx.beginPath();
        for (let i = a; i <= b; i++) {
          const [px, py] = toCanvas(trail[i][0], trail[i][1]);
          if (i === a) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }
      ctx.restore();
    }

    // Предметы: значок на светлой подложке, чтобы читался на любой части карты
    if (optsRef.current.showObjects) {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '15px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
      for (const o of objectsRef.current) {
        const [ox, oy] = toCanvas(o.x, o.y);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.beginPath();
        ctx.arc(ox, oy, 11, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#000';
        ctx.fillText(objectIcon(o.label), ox, oy + 1);
      }
      ctx.restore();
    }

    const p = poseRef.current;
    if (!p || Date.now() - p.at > POSE_STALE_MS) return;
    const { x, y, yaw } = p.pose;

    // Скан: точки в системе робота -> в систему карты
    ctx.fillStyle = '#ff5a5a';
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    for (const [deg, mm] of scanRef.current) {
      const a = (-deg * Math.PI) / 180; // по часовой -> против часовой
      const r = mm / 1000;
      const lx = r * Math.cos(a);
      const ly = r * Math.sin(a);
      const [px, py] = toCanvas(x + c * lx - s * ly, y + s * lx + c * ly);
      ctx.fillRect(px - 1, py - 1, 2, 2);
    }

    // Робот: треугольник носом по yaw (на холсте y вниз, поэтому минус)
    const [rx, ry] = toCanvas(x, y);
    const len = Math.max(8, (0.25 / msg.resolution) * scale);

    // Предполагаемое движение: по одометрии и по команде
    if (optsRef.current.showVector) {
      const drawMotion = (v: number, w: number, color: string, dashed: boolean) => {
        if (Math.abs(v) < 0.01 && Math.abs(w) < 0.05) return;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 2.5;
        ctx.setLineDash(dashed ? [6, 5] : []);
        if (Math.abs(v) < 0.01) {
          // Поворот на месте: дуга вокруг робота в сторону поворота
          const r = len * 1.7;
          const sweep = Math.max(-Math.PI * 0.9, Math.min(Math.PI * 0.9, w * PREDICT_S * 0.5));
          const a0 = -yaw;
          const a1 = -(yaw + sweep);
          ctx.beginPath();
          ctx.arc(rx, ry, r, a0, a1, sweep > 0);
          ctx.stroke();
          const ex = rx + r * Math.cos(a1);
          const ey = ry + r * Math.sin(a1);
          arrowHead(ctx, ex, ey, a1 + (sweep > 0 ? -Math.PI / 2 : Math.PI / 2), 9);
        } else {
          const pts = predictPath(p.pose, v, w).map(([px, py]) => toCanvas(px, py));
          ctx.beginPath();
          pts.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
          ctx.stroke();
          const [ex, ey] = pts[pts.length - 1];
          const [bx, by] = pts[pts.length - 2];
          arrowHead(ctx, ex, ey, Math.atan2(ey - by, ex - bx), 9);
        }
        ctx.restore();
      };
      const { v, w, cmd } = p.pose;
      // Команда пунктиром поверх: если робот едет как велено, видны обе линии
      if (v !== undefined && w !== undefined) drawMotion(v, w, '#2ee6a6', false);
      if (cmd) drawMotion(cmd.v, cmd.w, '#ff9f43', true);
    }
    ctx.save();
    ctx.translate(rx, ry);
    ctx.rotate(-yaw);
    ctx.fillStyle = '#ffdc50';
    ctx.strokeStyle = '#1e222c';
    ctx.beginPath();
    ctx.moveTo(len, 0);
    ctx.lineTo(-len * 0.6, len * 0.55);
    ctx.lineTo(-len * 0.6, -len * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  };

  const requestDraw = () => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      draw();
    });
  };

  // Холст — по размеру блока (с учётом плотности пикселей экрана)
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ro = new ResizeObserver(() => {
      const w = Math.max(50, wrap.clientWidth);
      const h = Math.max(50, wrap.clientHeight);
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { w, h };
      setNarrow(w < 600);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      draw();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mapRef.current = null;
    poseRef.current = null;
    scanRef.current = [];
    trailRef.current = [];
    trailLenRef.current = 0;
    objectsRef.current = [];
    setObjectCount(0);
    setTrailInfo({ points: 0, meters: 0 });
    setMapInfo(null);
    setHasPose(false);
    setSlam(null);
    requestDraw();
    if (!zenohSession) return;

    let cancelled = false;
    const subs: zenoh.Subscriber[] = [];
    const prefix = `robot/${robotId}`;

    const onMap = (sample: zenoh.Sample) => {
      let msg: MapMsg;
      try {
        msg = JSON.parse(payloadText(sample));
      } catch {
        return;
      }
      // Та же карта, что уже показана (повторная рассылка раз в несколько секунд)
      if (mapRef.current && mapRef.current.msg.timestamp === msg.timestamp) return;
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        mapRef.current = { msg, img };
        setMapInfo(`${(msg.width * msg.resolution).toFixed(1)} × ${(msg.height * msg.resolution).toFixed(1)} м, ${Math.round(msg.resolution * 100)} см/пикс`);
        requestDraw();
      };
      img.src = `data:image/png;base64,${msg.png}`;
    };
    const onPose = (sample: zenoh.Sample) => {
      let pose: Pose;
      try {
        pose = JSON.parse(payloadText(sample));
      } catch {
        return;
      }
      poseRef.current = { pose, at: Date.now() };
      const trail = trailRef.current;
      const last = trail[trail.length - 1];
      const step = last ? Math.hypot(pose.x - last[0], pose.y - last[1]) : Infinity;
      if (step >= TRAIL_STEP_M) {
        // Скачок больше 2 м — это перезапуск SLAM/релокализация, а не езда: в длину не считаем
        if (last && step < 2) trailLenRef.current += step;
        trail.push([pose.x, pose.y]);
        if (trail.length > TRAIL_MAX) trail.splice(0, trail.length - TRAIL_MAX);
        setTrailInfo({ points: trail.length, meters: trailLenRef.current });
      }
      setHasPose(true);
      requestDraw();
    };
    const onScan = (sample: zenoh.Sample) => {
      try {
        const points = JSON.parse(payloadText(sample)).points;
        if (Array.isArray(points)) {
          scanRef.current = points;
          requestDraw();
        }
      } catch {
        /* ignore */
      }
    };
    const onObjects = (sample: zenoh.Sample) => {
      try {
        const msg: MapObjectsMsg = JSON.parse(payloadText(sample));
        if (Array.isArray(msg.objects)) {
          objectsRef.current = msg.objects;
          setObjectCount(msg.objects.length);
          requestDraw();
        }
      } catch {
        /* ignore */
      }
    };
    const onMission = (sample: zenoh.Sample) => {
      try {
        const msg: MissionStatus = JSON.parse(payloadText(sample));
        missionRef.current = msg;
        if (msg.mode === 'goto') pendingGoalRef.current = null;
        setMission(msg);
        requestDraw();
      } catch {
        /* ignore */
      }
    };
    const onStatus = (sample: zenoh.Sample) => {
      try {
        const st = JSON.parse(payloadText(sample));
        setSlam(st.slam ?? null);
        setMapsSettings(st.settings?.maps ?? null);
      } catch {
        /* ignore */
      }
    };

    (async () => {
      const handlers: [string, (s: zenoh.Sample) => void][] = [
        [`${prefix}/map`, onMap],
        [`${prefix}/pose`, onPose],
        [`${prefix}/scan`, onScan],
        [`${prefix}/status`, onStatus],
        [`${prefix}/vision/objects`, onObjects],
        [`${prefix}/mission`, onMission],
      ];
      for (const [key, handler] of handlers) {
        try {
          const sub = await zenohSession.declareSubscriber(key, { handler });
          if (cancelled) await sub.undeclare();
          else subs.push(sub);
        } catch (err) {
          console.warn(`MapViewer: subscribe ${key} failed`, err);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      subs.forEach(sub => sub.undeclare().catch(err => console.warn('MapViewer: undeclare failed', err)));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zenohSession, robotId]);

  // Робот перестал присылать позу — убираем его с карты
  useEffect(() => {
    const t = setInterval(() => {
      const stale = !poseRef.current || Date.now() - poseRef.current.at > POSE_STALE_MS;
      setHasPose(!stale);
      if (stale) requestDraw();
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Перерисовка при переключении слоёв
  useEffect(() => {
    if (compact) {
      requestDraw();
      return;
    }
    writeFlag('map.trail', showTrail);
    writeFlag('map.vector', showVector);
    writeFlag('map.objects', showObjects);
    writeFlag('map.clickGo', clickGo);
    requestDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrail, showVector, showObjects, clickGo]);

  const sendGoal = (msg: unknown) =>
    zenohSession?.put(`robot/${robotId}/goal`, JSON.stringify(msg)).catch(err => console.warn('goal put failed', err));

  const local = (e: React.MouseEvent | React.PointerEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top] as [number, number];
  };

  const objectAt = (mx: number, my: number) => {
    const view = viewRef.current;
    if (!view || !optsRef.current.showObjects) return null;
    let best: { obj: MapObject; px: number; py: number } | null = null;
    let bestD = HIT_PX;
    for (const o of objectsRef.current) {
      const [px, py] = view.toCanvas(o.x, o.y);
      const d = Math.hypot(px - mx, py - my);
      if (d < bestD) {
        best = { obj: o, px, py };
        bestD = d;
      }
    }
    return best;
  };

  // Нажатие: робот — карточка, предмет — подпись, иначе (если включено) цель поездки
  const onMapClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (compact) {
      onActivate?.();
      return;
    }
    const view = viewRef.current;
    if (!view) return;
    const [mx, my] = local(e);
    const p = poseRef.current;
    if (p && Date.now() - p.at < POSE_STALE_MS) {
      const [rx, ry] = view.toCanvas(p.pose.x, p.pose.y);
      if (Math.hypot(rx - mx, ry - my) < HIT_PX) {
        setRobotCard({ px: rx, py: ry });
        return;
      }
    }
    const obj = objectAt(mx, my);
    if (obj) {
      setHovered({ ...obj, pinned: true });
      return;
    }
    setRobotCard(null);
    setHovered(null);
    if (!clickGo || !zenohSession) return;
    const [x, y] = view.toWorld(mx, my);
    pendingGoalRef.current = { x, y, at: Date.now() };
    sendGoal({ x: Number(x.toFixed(3)), y: Number(y.toFixed(3)) });
    requestDraw();
  };

  // Значок под мышью -> подпись
  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (compact || hovered?.pinned) return;
    const [mx, my] = local(e);
    setHovered(objectAt(mx, my));
  };

  const clearTrail = () => {
    const p = poseRef.current;
    trailRef.current = p ? [[p.pose.x, p.pose.y]] : [];
    trailLenRef.current = 0;
    setTrailInfo({ points: trailRef.current.length, meters: 0 });
    requestDraw();
  };

  const saveMap = async () => {
    if (!zenohSession) return;
    const name = mapName.trim();
    setSlam({ ok: null, name, saving: true });
    savePop.setOpen(false);
    try {
      await zenohSession.put(`robot/${robotId}/slam`, JSON.stringify({ cmd: 'save', ...(name ? { name } : {}) }));
    } catch (err) {
      setSlam({ ok: false, name, error: err instanceof Error ? err.message : String(err) });
    }
  };

  // Нет карты: вид лидара, если он есть (ESP32-робот без SLAM)
  const lidarInstead = !!zenohSession && !mapInfo && scanFresh;
  let overlay: string | null = null;
  if (!zenohSession) overlay = 'Нет связи с сервером';
  else if (!mapInfo && !lidarInstead) overlay = 'Карта недоступна (SLAM не запущен на роботе?)';

  let slamText: string | null = null;
  if (slam?.saving) slamText = `Сохранение «${slam.name || 'авто'}»…`;
  else if (slam?.loading) slamText = 'Открываю карту…';
  else if (slam?.ok && slam.path) slamText = `Карта сохранена: ${slam.path}`;
  else if (slam && slam.ok === false) slamText = `Ошибка: ${slam.error}`;

  const tooltip = hovered && (
    <div className={`map-tooltip ${hovered.pinned ? 'pinned' : ''}`} style={{ left: hovered.px, top: hovered.py - 16 }}
         onClick={() => setHovered(null)}>
      {objectIcon(hovered.obj.label)} {hovered.obj.label_ru}
      {hovered.obj.part ? ` · в ${hovered.obj.part}` : ''} · видел {hovered.obj.hits} раз
    </div>
  );

  return (
    <div className={`op-map ${compact ? 'compact' : ''}`}>
      <div ref={wrapRef} className="op-map-canvas">
        <canvas ref={canvasRef} style={{ cursor: compact ? 'pointer' : clickGo && mapInfo ? 'crosshair' : undefined }}
                onMouseMove={onMouseMove} onMouseLeave={() => !hovered?.pinned && setHovered(null)} onClick={onMapClick}
                role={compact ? 'button' : 'img'} aria-label={compact ? 'Карта: открыть' : `Карта SLAM${mapInfo ? `, ${mapInfo}` : ''}${clickGo ? '. Нажмите на карту, чтобы отправить робота в точку' : ''}`} />
        {lidarInstead && (
          <div className="op-map-lidar">
            <LidarViewer zenohSession={zenohSession} scanKey={`robot/${robotId}/scan`} />
            {!compact && <span className="op-map-note">Карты нет — показан лидар</span>}
          </div>
        )}
        {tooltip}
        {robotCard && <RobotCard x={robotCard.px} y={robotCard.py} pose={poseRef.current?.pose ?? null} onClose={() => setRobotCard(null)} />}
        {overlay && <div className="op-map-msg" onClick={compact ? onActivate : undefined}>{overlay}</div>}
        {compact && <span className="op-cam-badge">Карта</span>}
      </div>

      {!compact && (
        <>
          <div className="op-map-tools" role="toolbar" aria-label="Карта">
            {narrow ? (
              <>
                <button type="button" className={`op-tool ${clickGo ? 'on accent' : ''}`} aria-pressed={clickGo} onClick={() => setClickGo(v => !v)}
                        title="Клик по карте — робот строит путь и едет в эту точку">
                  <Icon name="target" size={15} /> Ехать по клику
                </button>
                <div className="op-pop-wrap" ref={menuPop.ref}>
                  <button type="button" className={`op-tool ${menuPop.open ? 'on' : ''}`} aria-expanded={menuPop.open} onClick={() => menuPop.setOpen(!menuPop.open)}>
                    <Icon name="layers" size={15} /> Слои и карты
                  </button>
                  {menuPop.open && (
                    <div className="op-popover op-map-menu" role="dialog" aria-label="Слои и карты">
                      <label className="op-check"><input type="checkbox" checked={showTrail} onChange={e => setShowTrail(e.target.checked)} />
                        След{trailInfo.points > 1 ? ` · ${trailInfo.meters.toFixed(1)} м` : ''}</label>
                      <label className="op-check"><input type="checkbox" checked={showVector} onChange={e => setShowVector(e.target.checked)} /> Вектор движения</label>
                      <label className="op-check"><input type="checkbox" checked={showObjects} onChange={e => setShowObjects(e.target.checked)} />
                        Предметы{objectCount ? ` · ${objectCount}` : ''}</label>
                      <button type="button" className="op-btn" onClick={clearTrail} disabled={trailInfo.points < 2}>Очистить след</button>
                      <div className="op-pop-title">Сохранить карту</div>
                      <div className="op-row">
                        <input type="text" value={mapName} onChange={e => setMapName(e.target.value)} aria-label="Имя карты"
                               placeholder="имя (необязательно)" onKeyDown={e => e.key === 'Enter' && saveMap()} />
                        <button type="button" className="op-btn primary" onClick={() => { saveMap(); menuPop.setOpen(false); }}
                                disabled={!zenohSession || !mapInfo || !!slam?.saving}>Сохранить</button>
                      </div>
                      <button type="button" className="op-btn" onClick={() => { setShowMaps(true); menuPop.setOpen(false); }} disabled={!zenohSession}>
                        <Icon name="folder" size={15} /> Сохранённые карты…
                      </button>
                      <p className="op-muted">{mapInfo ?? 'Нет карты'}{mapInfo && !hasPose ? ' · нет позы робота' : ''}</p>
                      <div className="map-legend">
                        <span><i style={{ background: '#ffdc50' }} /> робот</span>
                        <span><i style={{ background: '#2ee6a6' }} /> по одометрии</span>
                        <span><i className="dashed" style={{ borderColor: '#ff9f43' }} /> команда</span>
                        <span><i style={{ background: '#4fc3f7' }} /> след</span>
                        <span><i className="dashed" style={{ borderColor: '#b36bff' }} /> путь к точке</span>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button type="button" className={`op-tool ${showTrail ? 'on' : ''}`} aria-pressed={showTrail} onClick={() => setShowTrail(v => !v)}>
                  След{trailInfo.points > 1 ? ` · ${trailInfo.meters.toFixed(1)} м` : ''}
                </button>
                <button type="button" className={`op-tool ${showVector ? 'on' : ''}`} aria-pressed={showVector} onClick={() => setShowVector(v => !v)}>
                  Вектор
                </button>
                <button type="button" className="op-tool" onClick={clearTrail} disabled={trailInfo.points < 2}>Очистить след</button>
                <button type="button" className={`op-tool ${clickGo ? 'on accent' : ''}`} aria-pressed={clickGo} onClick={() => setClickGo(v => !v)}
                        title="Клик по карте — робот строит путь и едет в эту точку">
                  <Icon name="target" size={15} /> Ехать по клику
                </button>
                <button type="button" className={`op-tool ${showObjects ? 'on' : ''}`} aria-pressed={showObjects} onClick={() => setShowObjects(v => !v)}
                        title="Предметы, которые робот заметил по дороге">
                  Предметы{objectCount ? ` · ${objectCount}` : ''}
                </button>
                <div className="op-pop-wrap" ref={savePop.ref}>
                  <button type="button" className="op-tool" onClick={() => savePop.setOpen(!savePop.open)} disabled={!zenohSession || !mapInfo || !!slam?.saving}
                          aria-expanded={savePop.open}>
                    <Icon name="save" size={15} /> Сохранить
                  </button>
                  {savePop.open && (
                    <div className="op-popover" role="dialog" aria-label="Сохранить карту">
                      <div className="op-pop-title">Сохранить карту</div>
                      <div className="op-row">
                        <input type="text" value={mapName} onChange={e => setMapName(e.target.value)} aria-label="Имя карты"
                               placeholder="имя карты (необязательно)" onKeyDown={e => e.key === 'Enter' && saveMap()} autoFocus />
                        <button type="button" className="op-btn primary" onClick={saveMap}>Сохранить</button>
                      </div>
                    </div>
                  )}
                </div>
                <button type="button" className={`op-tool ${showMaps ? 'on' : ''}`} onClick={() => setShowMaps(v => !v)} disabled={!zenohSession}
                        title="Сохранённые карты: открыть, удалить, новая карта, автосохранение">
                  <Icon name="folder" size={15} /> Карты…
                </button>
                <div className="op-pop-wrap" ref={infoPop.ref}>
                  <button type="button" className="op-tool" aria-label="Легенда и сведения о карте" aria-expanded={infoPop.open}
                          onClick={() => infoPop.setOpen(!infoPop.open)}>
                    <Icon name="info" size={15} />
                  </button>
                  {infoPop.open && (
                    <div className="op-popover right" role="dialog" aria-label="Легенда карты">
                      <div className="op-pop-title">Карта</div>
                      <p className="op-muted">{mapInfo ?? 'Нет карты'}{mapInfo && !hasPose ? ' · нет позы робота' : ''}</p>
                      <div className="map-legend">
                        <span><i style={{ background: '#ffdc50' }} /> робот</span>
                        <span><i style={{ background: '#2ee6a6' }} /> движение по одометрии</span>
                        <span><i className="dashed" style={{ borderColor: '#ff9f43' }} /> команда</span>
                        <span><i style={{ background: '#4fc3f7' }} /> след</span>
                        <span><i style={{ background: '#ff5a5a' }} /> лидар</span>
                        <span><i className="dashed" style={{ borderColor: '#b36bff' }} /> путь к точке</span>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {(clickGo || mission?.mode === 'goto') && (
            <div className="op-map-banner map-goto" role="status">
              {mission?.mode === 'goto' ? (
                <>
                  <span>🎯 {mission.detail || 'еду к точке'}</span>
                  <button type="button" className="op-btn" onClick={() => sendGoal({ cmd: 'cancel' })}>Отменить</button>
                </>
              ) : mission?.result ? (
                <span className={mission.result === 'приехал' ? 'ok' : 'warn'}>
                  {mission.result === 'приехал' ? '✅ Приехал' : `⚠️ ${mission.result}`} · нажмите на карту, куда ехать
                </span>
              ) : (
                <span>Нажмите на карту, куда ехать</span>
              )}
            </div>
          )}

          {slamText && <div className={`op-map-banner bottom ${slam?.ok === false ? 'bad' : ''}`} role="status">{slamText}</div>}

          {showMaps && zenohSession && (
            <div className="op-map-drawer">
              <MapsPanel zenohSession={zenohSession} robotId={robotId} slamStatus={slam}
                         mapsSettings={mapsSettings} onClose={() => setShowMaps(false)} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

function arrowHead(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, size: number) {
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(x + size * Math.cos(angle), y + size * Math.sin(angle));
  ctx.lineTo(x + size * Math.cos(angle + 2.5), y + size * Math.sin(angle + 2.5));
  ctx.lineTo(x + size * Math.cos(angle - 2.5), y + size * Math.sin(angle - 2.5));
  ctx.closePath();
  ctx.fill();
}

export default MapViewer;
